import * as React from 'react'
import { ChevronsUpDown, Check } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { cn } from '@/lib/utils'
import { useAccounts } from '@/features/accounts/api/get-accounts'

type AccountComboboxProps = {
    label?: string
    name: string
    placeholder?: string
}

export default function AccountCombobox({ label = 'Account', name, placeholder = 'Select account' }: AccountComboboxProps) {
    const form = useFormContext()
    const { data, isLoading } = useAccounts({})
    const accounts = (data ?? []).map((a) => ({ label: a.name, value: a.id }))
    const [open, setOpen] = React.useState(false)

    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
                <FormItem className='flex flex-col w-full'>
                    {label && <FormLabel>{label}</FormLabel>}
                    <Popover open={open} onOpenChange={setOpen}>
                        <PopoverTrigger asChild>
                            <FormControl>
                                <Button
                                    variant='outline'
                                    role='combobox'
                                    className={cn('justify-between', !field.value && 'text-muted-foreground')}
                                >
                                    {field.value ? accounts.find((a) => a.value === field.value)?.label : (isLoading ? 'Loading…' : placeholder)}
                                    <ChevronsUpDown className='opacity-50' />
                                </Button>
                            </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className='p-0'>
                            <Command>
                                <CommandInput placeholder='Search accounts…' className='h-9' />
                                <CommandList>
                                    <CommandEmpty>No accounts found.</CommandEmpty>
                                    <CommandGroup>
                                        {accounts.map((acc) => (
                                            <CommandItem
                                                key={acc.value}
                                                value={acc.label}
                                                onSelect={() => {
                                                    form.setValue(name as any, acc.value, { shouldDirty: true })
                                                    setOpen(false)
                                                }}
                                            >
                                                {acc.label}
                                                <Check className={cn('ml-auto', acc.value === field.value ? 'opacity-100' : 'opacity-0')} />
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                    <FormMessage />
                </FormItem>
            )}
        />
    )
}

//